const appPrefix = 'C2';

/**
 * Authentication service for managing user data from localStorage
 */
class AuthService
{
  /**
   * Get personId from localStorage
   * @returns {number} Person ID or 0 if not found/invalid
   */
  static PersonId(): number
  {
    try
    {
      const personId = localStorage.getItem(`${appPrefix}_personid`);
      return personId ? parseInt(personId, 10) : 0;
    }
    catch (e)
    {
      console.error('Error getting PersonId from localStorage:', e);
      return 0;
    }
  }

  /**
   * Get GivenName from localStorage
   * @returns {string | null} Given name or null if not found
   */
  static GivenName(): string | null
  {
    return localStorage.getItem(`${appPrefix}_givenname`);
  }

  /**
   * Get ImageProfile from localStorage
   * @returns {string} Image profile URL or empty string if not found
   */
  static ImageProfile(): string
  {
    const imageProfile = localStorage.getItem(`${appPrefix}_imageprofile`);
    return imageProfile === null || imageProfile === '' ? '' : imageProfile;
  }

  /**
   * Set personId in localStorage
   * @param {number} personId - Person ID to store
   */
  static setPersonId(personId: number): void
  {
    try
    {
      localStorage.setItem(`${appPrefix}_personid`, personId.toString());
    }
    catch (e)
    {
      console.error('Error setting PersonId in localStorage:', e);
    }
  }

  /**
   * Set GivenName in localStorage
   * @param {string} givenName - Given name to store
   */
  static setGivenName(givenName: string): void
  {
    try
    {
      localStorage.setItem(`${appPrefix}_givenname`, givenName);
    }
    catch (e)
    {
      console.error('Error setting GivenName in localStorage:', e);
    }
  }

  /**
   * Set ImageProfile in localStorage
   * @param {string} imageProfile - Image profile URL to store
   */
  static setImageProfile(imageProfile: string): void
  {
    try
    {
      localStorage.setItem(`${appPrefix}_imageprofile`, imageProfile);
    }
    catch (e)
    {
      console.error('Error setting ImageProfile in localStorage:', e);
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has valid personId
   */
  static isAuthenticated(): boolean
  {
    const personId = this.PersonId();
    return personId > 0;
  }

  /**
   * Get all auth data
   * @returns {Object} Object containing all auth data
   */
  static getAuthData(): {
    personId: number;
    givenName: string | null;
    imageProfile: string;
    }
  {
    return {
      personId: this.PersonId(),
      givenName: this.GivenName(),
      imageProfile: this.ImageProfile(),
    };
  }
}

export default AuthService;

// Export individual methods for easier testing and tree-shaking
export const {
  PersonId,
  GivenName,
  ImageProfile,
  setPersonId,
  setGivenName,
  setImageProfile,
  isAuthenticated,
  getAuthData,
} = AuthService;
